/**
 * remManager - 统一管理 flexible rem 覆盖策略与 token
 *
 * 目标：
 * - 把 scattered 的 window.flexibleOverridePush/Pop 调用收敛到一个地方
 * - 路由级策略（例如 /natvip 动态渲染 SkinReport）在这里统一判定
 * - 组件级临时覆盖（例如 aiEnter 弹窗）也通过这里统一申请/释放
 */

const STORE_KEY = '__remManagerStore__';

function getStore() {
  if (typeof window === 'undefined') return { tokens: {} };
  if (!window[STORE_KEY]) {
    window[STORE_KEY] = { tokens: {} };
  }
  return window[STORE_KEY];
}

function canPush() {
  return typeof window !== 'undefined' && typeof window.flexibleOverridePush === 'function';
}

function canPop() {
  return typeof window !== 'undefined' && typeof window.flexibleOverridePop === 'function';
}

function routeToPlain(route) {
  // 兼容 vue-router 2/3 的 route 结构
  const r = route || {};
  return {
    path: r.path || '',
    query: (r.query || {}) ?? {}
  };
}

/**
 * 申请一个 rem 覆盖（同 key 幂等）
 * @param {string} key 唯一 key（例如 'route:natvip-skinreport' / 'ui:aiEnter'）
 * @param {'lib'|'custom'|'pc'} type
 * @param {number=} parentWidth 可选：pc 模式可传容器宽
 */
export function setRemOverride(key, type, parentWidth) {
  if (!key) return '';
  const store = getStore();
  if (store.tokens[key]) return store.tokens[key];
  if (!canPush()) return '';
  const token = window.flexibleOverridePush(type, parentWidth);
  store.tokens[key] = token;
  return token;
}

/**
 * 释放一个 rem 覆盖（按 key）
 */
export function clearRemOverride(key) {
  if (!key) return;
  const store = getStore();
  const token = store.tokens[key];
  if (!token) return;
  if (canPop()) {
    window.flexibleOverridePop(token);
  }
  store.tokens[key] = '';
}

/**
 * 路由级 rem 策略：在这里统一处理 “哪些路由需要 pc/custom/lib 覆盖”
 *
 * 当前策略：
 * - /natvip 动态渲染 SkinReport(reportType=17)：强制 pc rem（对齐 pcResize）
 * - /skinReport（如果未来直接走该路由）：同样强制 pc rem
 *
 * 你可以把更多 “特殊页面” 规则都收敛到这里。
 */
export function applyRemPolicyForRoute(route) {
  if (typeof window === 'undefined') return;
  const { path, query } = routeToPlain(route);

  const reportType = query?.reportType;
  const isNatvipSkinReport = path === '/natvip' && String(reportType) === '17';
  const isSkinReportRoute = path === '/skinReport';

  const needsPc = isNatvipSkinReport || isSkinReportRoute;

  const ROUTE_PC_KEY = 'route:pc';
  if (needsPc) {
    setRemOverride(ROUTE_PC_KEY, 'pc');
  } else {
    clearRemOverride(ROUTE_PC_KEY);
  }

  // 最后统一触发一次 rem 刷新（会自动应用 override 栈顶规则）
  if (typeof window.flexibleRouteChange === 'function') {
    window.flexibleRouteChange();
  }
}



// WEBPACK FOOTER //
// ./src/utils/remManager.js